import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { del, get, patch, post } from "../api/client";
import { useLoad } from "../api/hooks";
import type { MyInvite, TeamDetail, TeamMember, TeamSummary } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { cleanHandleInput, initialsOf } from "../components/HandleSheet";
import { Shell } from "../components/Shell";
import { Dot, ErrorLine, Sheet, useToast } from "../components/ui";
import { useIsDesktop } from "../lib/device";
import { fmtDate } from "../lib/format";
import { planName } from "./Videos";

export default function TeamsPage() {
  const { teamId } = useParams<{ teamId?: string }>();
  if (teamId) return <TeamDetailView teamId={teamId} />;
  return <TeamsListView />;
}

function TeamsListView() {
  const { me, refresh } = useAuth();
  const nav = useNavigate();
  const desktop = useIsDesktop();
  const [toast, say] = useToast();
  const teamsQ = useLoad<{ items: TeamSummary[] }>(() => get("/v1/teams"), []);
  const invitesQ = useLoad<{ items: MyInvite[] }>(() => get("/v1/me/invites"), []);
  const [createOpen, setCreateOpen] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);

  const canCreate = me?.can_create !== false;
  const teams = teamsQ.data?.items ?? [];
  const invites = invitesQ.data?.items ?? [];
  const meTeamsMap = new Map((me?.teams ?? []).map((t) => [t.id, t]));
  const mgrCount = teams.filter((t) => t.role === "manager").length;
  const memCount = teams.filter((t) => t.role === "member").length;

  const createTeam = async (e: FormEvent) => {
    e.preventDefault();
    const name = teamName.trim();
    if (!name || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await post<TeamDetail>("/v1/teams", { name });
      await refresh();
      setCreateOpen(false);
      setTeamName("");
      nav(`/teams/${res.team.id}`);
    } catch (e) {
      setErr(e as Error);
    } finally {
      setBusy(false);
    }
  };

  const acceptInvite = async (inv: MyInvite) => {
    setBusy(true);
    try {
      const res = await post<{ team_id: string; role: string; video_id: string | null }>(`/v1/me/invites/${inv.id}/accept`);
      await Promise.all([refresh(), teamsQ.reload(), invitesQ.reload()]);
      say(`Joined ${inv.team.name}`);
      if (res.video_id && me?.user.handle) {
        nav(`/u/${me.user.handle}/clips?video=${res.video_id}`);
      }
    } catch (e) {
      say((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const declineInvite = async (inv: MyInvite) => {
    setBusy(true);
    try {
      await post(`/v1/me/invites/${inv.id}/decline`);
      await Promise.all([refresh(), invitesQ.reload()]);
      say("Invite declined.");
    } catch (e) {
      say((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const mobileSubline = (() => {
    const parts: string[] = [];
    if (me?.user.handle) parts.push(`@${me.user.handle}`);
    if (mgrCount > 0) parts.push(`Manager of ${mgrCount}`);
    if (memCount > 0) parts.push(`Member of ${memCount}`);
    if (parts.length <= 1) return "Create a team to upload matches and tag players on their clips by @handle.";
    return `${parts[0]} · ${parts.slice(1).join(", ")}`;
  })();

  return (
    <Shell brand active="Teams">
      <div className="p" style={{ paddingTop: desktop ? 48 : 16, paddingBottom: 40 }} data-testid="teams-page">
        <div className="row between">
          <div>
            <h1 style={desktop ? { fontSize: 32 } : undefined}>Teams</h1>
            {desktop && (
              <p className="sub mt6">
                Create a team to upload matches and tag players on their clips by @handle.
              </p>
            )}
          </div>
          {canCreate && (
            <button
              type="button"
              className={desktop ? "btn auto" : "btn sm"}
              onClick={() => setCreateOpen(true)}
              data-testid="create-team-btn"
            >
              Create team
            </button>
          )}
        </div>
        {!desktop && <p className="tiny mt6">{mobileSubline}</p>}

        {invites.length > 0 && (
          <div className="mt20 stack" style={{ gap: 10 }} data-testid="my-invites">
            {invites.map((inv) => (
              <div key={inv.id} className="inv-banner" data-testid={`invite-banner-${inv.id}`}>
                <div>
                  <div className="med">
                    Invited to <b>{inv.team.name}</b> as {inv.role === "manager" ? "Manager" : "Member"}
                  </div>
                  <div className="tiny mt4">
                    From {inv.invited_by.display_name}
                    {inv.invited_by.handle ? ` (@${inv.invited_by.handle})` : ""}
                    {inv.match_title ? ` · ${inv.match_title}` : ""}
                  </div>
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="btn dark sm"
                    disabled={busy}
                    onClick={() => acceptInvite(inv)}
                    data-testid={`accept-invite-${inv.id}`}
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    className="btn ghost sm"
                    disabled={busy}
                    onClick={() => declineInvite(inv)}
                    data-testid={`decline-invite-${inv.id}`}
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <ErrorLine error={teamsQ.error} />

        {teamsQ.loading && !teamsQ.data ? (
          <p className="sub mt24">Loading teams…</p>
        ) : teams.length === 0 ? (
          <div className="notice mt24" style={desktop ? { maxWidth: 640 } : undefined} data-testid="teams-empty">
            <div className="med">You are not on a team yet.</div>
            <p className="tiny mt6">
              {canCreate
                ? "Use Create team above to set up your team, upload matches, and assign batting and bowling clips to your players' @handles."
                : "When a team manager assigns your clips or invites you, the team will appear here."}
            </p>
          </div>
        ) : (
          <div className={desktop ? "grid2 mt24" : "mt20 stack"} style={desktop ? undefined : { gap: 16 }} data-testid="teams-list">
            {teams.map((t) => {
              const meta = meTeamsMap.get(t.id);
              const usage = meta?.usage;
              const totalPlayers = t.members;
              return (
                <div key={t.id} className="card" data-testid={`team-card-${t.id}`}>
                  <div className="row between">
                    <Link to={`/teams/${t.id}`} style={{ fontSize: 18, fontWeight: 500 }}>
                      {t.name}
                    </Link>
                    <span className={`pill ${t.role === "manager" ? "mgr" : ""}`}>
                      {t.role === "manager" ? "Manager" : "Member"}
                    </span>
                  </div>
                  <p className="sub mt6">
                    {totalPlayers} {totalPlayers === 1 ? "player" : "players"}
                    {t.managers > 0 ? ` · ${t.managers} ${t.managers === 1 ? "manager" : "managers"}` : ""}
                    {t.is_billing_owner ? " · Billing owner" : ""}
                  </p>
                  {t.role === "manager" && usage ? (
                    <>
                      <div className="tiny mt10">
                        <Dot kind="on" />
                        {usage.videos_used} of {usage.videos_per_month} videos this month · {planName(meta?.plan_code ?? "free")}
                        {me?.user.handle && t.is_billing_owner ? ` (@${me.user.handle})` : ""}
                      </div>
                      <div className="bar mt8">
                        <i style={{ width: `${Math.min(100, Math.round((usage.videos_used / Math.max(1, usage.videos_per_month)) * 100))}%` }} />
                      </div>
                    </>
                  ) : (
                    <div className="tiny mt10">
                      <Dot kind="ok" />
                      Watch and download clips assigned to your @handle for free
                    </div>
                  )}
                  {t.role === "manager" ? (
                    <div className="grid2 mt16">
                      <button
                        type="button"
                        className="btn ghost sm"
                        style={{ width: "100%" }}
                        onClick={() => nav(`/teams/${t.id}`)}
                        data-testid={`open-team-${t.id}`}
                      >
                        Open team
                      </button>
                      <button
                        type="button"
                        className="btn dark sm"
                        style={{ width: "100%" }}
                        onClick={() => nav(`/upload?team=${t.id}`)}
                      >
                        Upload match
                      </button>
                    </div>
                  ) : (
                    <div className={me?.user.handle ? "grid2 mt16" : "mt16"}>
                      <button
                        type="button"
                        className="btn ghost sm"
                        style={{ width: "100%" }}
                        onClick={() => nav(`/teams/${t.id}`)}
                        data-testid={`open-team-${t.id}`}
                      >
                        Open team
                      </button>
                      {me?.user.handle && (
                        <button
                          type="button"
                          className="btn ghost sm"
                          style={{ width: "100%" }}
                          onClick={() => nav(`/u/${me.user.handle}?team=${t.id}`)}
                        >
                          Your {t.name} clips
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {createOpen && (
        <Sheet onClose={() => !busy && setCreateOpen(false)}>
          <form onSubmit={createTeam} data-testid="create-team-sheet">
            <h2>Create a team</h2>
            <p className="sub mt8">You become the manager. Matches uploaded to this team count toward your plan.</p>
            <div className="mt20">
              <label className="tiny" style={{ display: "block", marginBottom: 6 }} htmlFor="new-team-name">
                Team name
              </label>
              <input
                id="new-team-name"
                className="field"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="Tuesday Nets"
                maxLength={60}
                required
                data-testid="team-name-input"
              />
            </div>
            <p className="tiny mt10">Players you add watch and download clips assigned to them for free.</p>
            <ErrorLine error={err} />
            <div className="mt24">
              <button type="submit" className="btn dark full" disabled={busy || !teamName.trim()} data-testid="create-team-submit">
                {busy ? "Creating…" : "Create team"}
              </button>
            </div>
            <div className="mt10">
              <button type="button" className="btn ghost full" onClick={() => setCreateOpen(false)} disabled={busy}>
                Cancel
              </button>
            </div>
          </form>
        </Sheet>
      )}
      {toast}
    </Shell>
  );
}

function TeamDetailView({ teamId }: { teamId: string }) {
  const { me, refresh } = useAuth();
  const nav = useNavigate();
  const desktop = useIsDesktop();
  const [toast, say] = useToast();
  const detailQ = useLoad<TeamDetail>(() => get(`/v1/teams/${teamId}`), [teamId]);
  const [addOpen, setAddOpen] = useState(false);
  const [addMode, setAddMode] = useState<"handle" | "invite">("handle");
  const [handleIn, setHandleIn] = useState("");
  const [emailIn, setEmailIn] = useState("");
  const [roleIn, setRoleIn] = useState<"member" | "manager">("member");
  const [manageMember, setManageMember] = useState<TeamMember | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);

  const data = detailQ.data;
  const team = data?.team;
  const isManager = team?.role === "manager";

  const addOrInvite = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !team) return;
    const cleanH = cleanHandleInput(handleIn);
    if (!cleanH) return;
    setBusy(true);
    setErr(null);
    try {
      if (addMode === "handle" && !emailIn.trim()) {
        await post(`/v1/teams/${team.id}/members`, { handle: cleanH, role: roleIn });
        say(`Added @${cleanH}`);
      } else {
        await post(`/v1/teams/${team.id}/invites`, {
          handle: cleanH,
          email: emailIn.trim(),
          role: roleIn,
        });
        say(`Invite sent to ${emailIn.trim()}`);
      }
      setAddOpen(false);
      setHandleIn("");
      setEmailIn("");
      setRoleIn("member");
      await detailQ.reload();
    } catch (e) {
      setErr(e as Error);
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (m: TeamMember, nextRole: "manager" | "member") => {
    if (!team || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await patch(`/v1/teams/${team.id}/members/${m.user_id}`, { role: nextRole });
      setManageMember(null);
      await Promise.all([detailQ.reload(), refresh()]);
      say(`${m.handle ? "@" + m.handle : m.display_name} is now ${nextRole}.`);
    } catch (e) {
      setErr(e as Error);
    } finally {
      setBusy(false);
    }
  };

  const offerBilling = async (m: TeamMember) => {
    if (!team || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await patch(`/v1/teams/${team.id}`, { pending_billing_user_id: m.user_id });
      setManageMember(null);
      await detailQ.reload();
      say(`Offered billing ownership to ${m.handle ? "@" + m.handle : m.display_name}.`);
    } catch (e) {
      setErr(e as Error);
    } finally {
      setBusy(false);
    }
  };

  const acceptBilling = async () => {
    if (!team || busy) return;
    setBusy(true);
    try {
      await post(`/v1/teams/${team.id}/accept-billing`);
      await Promise.all([detailQ.reload(), refresh()]);
      say("You are now the billing owner.");
    } catch (e) {
      say((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (m: TeamMember) => {
    if (!team || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await del(`/v1/teams/${team.id}/members/${m.user_id}`);
      setManageMember(null);
      if (m.user_id === me?.user.id) {
        await refresh();
        nav("/teams");
        return;
      }
      await detailQ.reload();
      say(`Removed ${m.handle ? "@" + m.handle : m.display_name}.`);
    } catch (e) {
      setErr(e as Error);
    } finally {
      setBusy(false);
    }
  };

  const resendInvite = async (invId: string) => {
    if (!team || busy) return;
    setBusy(true);
    try {
      await post(`/v1/teams/${team.id}/invites/${invId}/resend`);
      await detailQ.reload();
      say("Invite resent.");
    } catch (e) {
      say((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const revokeInvite = async (invId: string) => {
    if (!team || busy) return;
    setBusy(true);
    try {
      await del(`/v1/teams/${team.id}/invites/${invId}`);
      await detailQ.reload();
      say("Invite cancelled.");
    } catch (e) {
      say((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const deleteTeam = async () => {
    if (!team || busy) return;
    setBusy(true);
    try {
      await del(`/v1/teams/${team.id}`);
      await refresh();
      nav("/teams", { replace: true });
    } catch (e) {
      say((e as Error).message);
      setBusy(false);
    }
  };

  const membersList = data && team && (
    <div className="list mt12" data-testid="team-members">
      {data.members.map((m) => {
        const isMe = m.user_id === me?.user.id;
        const canManageRow =
          (team.is_billing_owner && !m.is_billing_owner) ||
          (isManager && m.role === "member") ||
          (isMe && !m.is_billing_owner);
        const metaParts: string[] = [m.role === "manager" ? "Manager" : "Member"];
        if (m.is_billing_owner) {
          metaParts.push(`Billing owner (${planName(team.plan_code)} · ${team.managers} of ${team.seats} ${team.seats === 1 ? "manager" : "managers"})`);
        }
        return (
          <div key={m.user_id} data-testid={`member-row-${m.handle ?? m.user_id}`}>
            <span className="avatar soft">{initialsOf(m.display_name, m.handle)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="med">
                {m.display_name}
                {isMe ? " (you)" : ""}{" "}
                {m.handle && (
                  <Link className="sub" style={{ fontWeight: 400 }} to={`/u/${m.handle}`}>
                    @{m.handle}
                  </Link>
                )}
              </div>
              <div className="tiny">{metaParts.join(" · ")}</div>
            </div>
            <span className={`pill ${m.role === "manager" ? "mgr" : ""}`}>
              {m.role === "manager" ? "Manager" : "Member"}
            </span>
            {m.is_billing_owner ? (
              <span className="tiny">Owner</span>
            ) : canManageRow ? (
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => {
                  setErr(null);
                  setManageMember(m);
                }}
                data-testid={`manage-member-${m.handle ?? m.user_id}`}
              >
                {isMe && !isManager ? "Leave" : "Manage"}
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  const invitesSection = data && team && isManager && data.invites && data.invites.length > 0 && (
    <div className={desktop ? "" : "mt24"} data-testid="team-invites">
      <h3>Pending invites · {data.invites.length}</h3>
      <div className="list mt12">
        {data.invites.map((inv) => (
          <div key={inv.id} style={{ flexWrap: "wrap" }} data-testid={`invite-row-${inv.handle}`}>
            <span className="avatar soft" style={{ color: "var(--blue)" }}>@</span>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div className="med">
                @{inv.handle} <span className="tiny">· {inv.email}</span>
              </div>
              <div className="tiny">
                {inv.match_title ? `${inv.match_title} · ` : ""}
                Invited as {inv.role}
              </div>
            </div>
            <div className="row" style={{ gap: 8, marginLeft: "auto" }}>
              <button type="button" className="btn ghost sm" disabled={busy} onClick={() => resendInvite(inv.id)}>
                Resend
              </button>
              <button
                type="button"
                className="btn ghost sm"
                disabled={busy}
                onClick={() => revokeInvite(inv.id)}
                data-testid={`revoke-invite-${inv.handle}`}
              >
                Cancel
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const teamSideCard = team && (
    <div className={`card ${isManager && data?.invites && data.invites.length > 0 ? "mt20" : ""}`}>
      <div className="med">Team clips & matches</div>
      <p className="tiny mt6">
        {team.managers} of {team.seats} {team.seats === 1 ? "manager" : "managers"} · {team.members}{" "}
        {team.members === 1 ? "member" : "members"}
      </p>
      <div className="row mt12" style={{ gap: 14, flexWrap: "wrap" }}>
        {me?.user.handle && (
          <Link className="u tiny" to={`/u/${me.user.handle}?team=${team.id}`}>
            Open on /u/{me.user.handle}
          </Link>
        )}
        {team.is_billing_owner && (
          <button
            type="button"
            className="u tiny"
            style={{ color: "var(--bad)" }}
            onClick={() => setConfirmDelete(true)}
            data-testid="delete-team-btn"
          >
            Delete team
          </button>
        )}
      </div>
    </div>
  );

  return (
    <Shell
      title={team?.name ?? "Team"}
      back="/teams"
      active="Teams"
      right={
        isManager && team ? (
          <button className="btn sm m-only" onClick={() => nav(`/upload?team=${team.id}`)}>
            Upload
          </button>
        ) : undefined
      }
    >
      <div className="p" style={{ paddingTop: desktop ? 36 : 8, paddingBottom: 40 }} data-testid="team-detail">
        <ErrorLine error={detailQ.error} />
        {!team ? (
          detailQ.loading ? <p className="sub">Loading…</p> : null
        ) : (
          <>
            {desktop && (
              <div className="tiny">
                <Link className="u" to="/teams">
                  Teams
                </Link>{" "}
                / {team.name}
              </div>
            )}
            <div className={`row between ${desktop ? "mt6" : ""}`}>
              <div className="row" style={{ gap: desktop ? 12 : 8 }}>
                <h1 style={desktop ? { fontSize: 32 } : undefined}>{team.name}</h1>
                {team.role && (
                  <span className={`pill ${team.role === "manager" ? "mgr" : ""}`}>
                    {team.role === "manager" ? "Manager" : "Member"}
                  </span>
                )}
              </div>
              {desktop && (
                <div className="row">
                  {isManager && (
                    <button
                      type="button"
                      className="btn ghost auto"
                      onClick={() => {
                        setErr(null);
                        setAddOpen(true);
                      }}
                      data-testid="add-or-invite-btn"
                    >
                      + Add or invite player
                    </button>
                  )}
                  {isManager && (
                    <button type="button" className="btn auto" onClick={() => nav(`/upload?team=${team.id}`)}>
                      Upload match
                    </button>
                  )}
                  {!isManager && me?.user.handle && (
                    <button type="button" className="btn ghost auto" onClick={() => nav(`/u/${me.user.handle}?team=${team.id}`)}>
                      Your {team.name} clips
                    </button>
                  )}
                </div>
              )}
            </div>

            {team.pending_billing_user?.id === me?.user.id && (
              <div className="inv-banner mt16" data-testid="pending-billing-banner">
                <div>
                  <div className="med">Billing ownership transfer requested</div>
                  <div className="tiny mt4">Accept to cover this team's future uploads under your plan.</div>
                </div>
                <button className="btn dark sm" onClick={acceptBilling} disabled={busy} data-testid="accept-billing-btn">
                  Accept billing
                </button>
              </div>
            )}

            <div className={desktop ? "notice mt16" : "notice mt12"} style={desktop ? { maxWidth: 760 } : undefined}>
              Plan owned by{" "}
              <b>
                {team.billing_user?.handle
                  ? `@${team.billing_user.handle}`
                  : (team.billing_user?.display_name ?? "owner")}
              </b>
              {desktop && team.billing_user?.display_name && team.billing_user?.handle
                ? ` (${team.billing_user.display_name})`
                : ""}{" "}
              · {planName(team.plan_code)}
              {team.usage
                ? ` · ${team.usage.videos_used} of ${team.usage.videos_per_month} videos this month, resets ${fmtDate(team.usage.period_end)}.`
                : "."}{" "}
              Team players watch and download clips assigned to them for free ({team.seats === 1 ? "1 manager" : `up to ${team.seats} managers`} on{" "}
              {planName(team.plan_code)}
              {team.plan_code !== "pro" ? ", up to 5 managers on Pro" : ""}).
              {team.is_billing_owner && (
                <>
                  {" "}
                  · <Link to="/plans" className="u">Manage plan</Link>
                </>
              )}
            </div>

            {desktop ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.35fr 1fr",
                  gap: 40,
                  marginTop: 32,
                  alignItems: "start",
                }}
              >
                <div>
                  <h3>Team members · {data.members.length}</h3>
                  {membersList}
                </div>
                <div>
                  {invitesSection}
                  {teamSideCard}
                </div>
              </div>
            ) : (
              <>
                <div className="row between mt24">
                  <h3>Team members · {data.members.length}</h3>
                  {isManager && (
                    <button
                      type="button"
                      className="btn ghost sm"
                      onClick={() => {
                        setErr(null);
                        setAddOpen(true);
                      }}
                      data-testid="add-or-invite-btn"
                    >
                      + Add or invite
                    </button>
                  )}
                </div>
                {membersList}
                {invitesSection}
                <div className="mt24">{teamSideCard}</div>
              </>
            )}
          </>
        )}
      </div>

      {addOpen && team && (
        <Sheet onClose={() => !busy && setAddOpen(false)}>
          <form onSubmit={addOrInvite} data-testid="add-member-sheet">
            <h2>Add or invite to {team.name}</h2>
            <div className="seg mt16">
              <button
                type="button"
                className={addMode === "handle" ? "on" : ""}
                onClick={() => {
                  setAddMode("handle");
                  setErr(null);
                }}
              >
                Existing @handle
              </button>
              <button
                type="button"
                className={addMode === "invite" ? "on" : ""}
                onClick={() => {
                  setAddMode("invite");
                  setErr(null);
                }}
                data-testid="tab-invite-email"
              >
                Invite by email
              </button>
            </div>
            <label className="tiny mt16" style={{ display: "block" }}>
              {addMode === "handle" ? "User @handle" : "Suggested @handle"}
            </label>
            <div className="handle-field mt6">
              <span className="handle-at">@</span>
              <input
                className="field handle-in"
                value={handleIn}
                onChange={(e) => setHandleIn(e.target.value.replace(/^@+/, "").toLowerCase())}
                placeholder="kiran"
                required
                data-testid="member-handle-input"
              />
            </div>
            {addMode === "invite" && (
              <>
                <label className="tiny mt16" style={{ display: "block" }}>
                  Email address
                </label>
                <input
                  type="email"
                  className="field mt6"
                  value={emailIn}
                  onChange={(e) => setEmailIn(e.target.value)}
                  placeholder="kiran@gmail.com"
                  required
                  data-testid="member-email-input"
                />
              </>
            )}
            <label className="tiny mt16" style={{ display: "block" }}>
              Role
            </label>
            <select
              className="field mt6"
              value={roleIn}
              onChange={(e) => setRoleIn(e.target.value as "member" | "manager")}
              data-testid="member-role-select"
            >
              <option value="member">Member (views & downloads clips free)</option>
              {team.is_billing_owner && <option value="manager">Manager (uploads matches & assigns clips)</option>}
            </select>
            <ErrorLine error={err} />
            <div className="mt20">
              <button type="submit" className="btn dark full" disabled={busy || !cleanHandleInput(handleIn)} data-testid="member-submit-btn">
                {busy ? "Saving…" : addMode === "handle" ? "Add to team" : "Send invite"}
              </button>
            </div>
            <div className="mt10">
              <button type="button" className="btn ghost full" onClick={() => setAddOpen(false)} disabled={busy}>
                Cancel
              </button>
            </div>
          </form>
        </Sheet>
      )}

      {manageMember && team && (
        <Sheet onClose={() => !busy && setManageMember(null)}>
          <div data-testid="manage-member-sheet">
            <h2>{manageMember.display_name}</h2>
            <p className="sub mt4">
              {manageMember.handle ? `@${manageMember.handle}` : ""} · {manageMember.role}
            </p>
            <ErrorLine error={err} />
            <div className="mt20 stack" style={{ gap: 10 }}>
              {team.is_billing_owner &&
                manageMember.user_id !== me?.user.id &&
                (manageMember.role === "member" ? (
                  <button
                    type="button"
                    className="btn dark full"
                    disabled={busy}
                    onClick={() => changeRole(manageMember, "manager")}
                    data-testid="promote-manager-btn"
                  >
                    Make Manager ({team.managers} of {team.seats} used)
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn ghost full"
                    disabled={busy}
                    onClick={() => changeRole(manageMember, "member")}
                    data-testid="demote-member-btn"
                  >
                    Change to Member
                  </button>
                ))}
              {team.is_billing_owner && manageMember.role === "manager" && manageMember.user_id !== me?.user.id && (
                <button
                  type="button"
                  className="btn ghost full"
                  disabled={busy}
                  onClick={() => offerBilling(manageMember)}
                  data-testid="offer-billing-btn"
                >
                  Transfer billing ownership
                </button>
              )}
              <button
                type="button"
                className="btn ghost full"
                disabled={busy}
                onClick={() => removeMember(manageMember)}
                data-testid="remove-member-btn"
              >
                {manageMember.user_id === me?.user.id ? `Leave ${team.name}` : "Remove from team"}
              </button>
              <button type="button" className="btn ghost full" onClick={() => setManageMember(null)} disabled={busy}>
                Cancel
              </button>
            </div>
          </div>
        </Sheet>
      )}

      {confirmDelete && team && (
        <Sheet onClose={() => !busy && setConfirmDelete(false)}>
          <div data-testid="delete-team-sheet">
            <h2>Delete {team.name}?</h2>
            <p className="sub mt10">Match videos stay with their uploader, and team assignments on those matches will be cleared.</p>
            <div className="mt20">
              <button className="btn dark full" onClick={deleteTeam} disabled={busy} data-testid="confirm-delete-team">
                {busy ? "Deleting…" : "Delete team"}
              </button>
            </div>
            <div className="mt10">
              <button type="button" className="btn ghost full" onClick={() => setConfirmDelete(false)} disabled={busy}>
                Cancel
              </button>
            </div>
          </div>
        </Sheet>
      )}
      {toast}
    </Shell>
  );
}
